import "./App.css";
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Modal,
  Alert,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import ReCAPTCHA from "react-google-recaptcha";
import {
  getAppOnlyBearerToken,
  saveToSharePointAsync,
  uploadFileToSharePointAsync,
} from "./SharePoint.Service";
import { verifyCaptchaAsync } from "./Captcha.Service";
import guardStopSign from "./assets/guard-stop-sign.jpg";

const initialFormState = {
  firstName: "",
  middleName: "",
  lastName: "",
  phoneNumber: "",
  alternativeNumber: "",
  email: "",
  postalCode: "",
  currentIntersection: "",
  availability: "",
  employmentStatus: "",
  yearsOfExperience: "",
  hasVulnerableSectorCheck: "",
  expiryDate: "",
  hasTransportation: "",
  hasSmartphone: "",
  comments: "",
  attachment: null,
  weatherComfort: "",
  physicalAbility: "",
  independentWork: "",
  publicInteraction: "",
  stopPaddleAbility: "",
  isCurrentCrossingGuard: "",
  continueRole: "",
  startAvailability: "",
  preferredStartDate: "",
  cityMunicipality: "", // New field
};

function App() {
  const [formValues, setFormValues] = useState(initialFormState);
  const [validated, setValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const recaptchaRef = React.createRef();
  const [captchaToken, setCaptchaToken] = useState("");
  const [token, setToken] = useState(null);

  useEffect(() => {
    const getToken = async () => {
      const token = await getAppOnlyBearerToken();
      setToken(token);
    };

    getToken();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormValues({ ...formValues, attachment: file });
  };

  const sanitizeFileName = (fileName) => {
    const timestamp = Date.now();
    const sanitizedFileName = fileName.replace(/[^\w\s.-]/g, "_");
    return `${timestamp}_${sanitizedFileName}`;
  };

  const handleSubmit = async (event) => {
    if (!captchaToken) {
      event.preventDefault();
      event.stopPropagation();
      setSubmitting(false);
      setError(true);
      setErrorMessage(
        "Please complete the reCaptcha verification to proceed."
      );
      return;
    }
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
      setShowError(true);
    } else {
      setShowError(false);
      event.preventDefault();

      if (token) {
        setShowThankYou(false);
        setSubmitting(true);
        verifyCaptchaAsync(captchaToken)
          .then(() => submitToSharePoint())
          .catch((error) => {
            console.log("Error uploading file to SharePoint:", error);
            setSubmitting(false);
            setError(true);
            setErrorMessage(
              "Error uploading file to SharePoint: " + error.message
            );
          });
      } else {
        console.log("Bearer token not available");
      }
    }
    setValidated(true);
  };

  const submitToSharePoint = () => {
    if (formValues.attachment) {
      const sanitizedFileName = sanitizeFileName(formValues.attachment.name);
      uploadFileToSharePointAsync(formValues.attachment, sanitizedFileName, token)
        .then((itemId) => {
          formValues.attachmentId = itemId;
          saveToSharePointAsync(formValues, token)
            .then(() => {
              console.log("Data saved to SharePoint");
              setSubmitting(false);
              setShowThankYou(true);
            })
            .catch((error) => {
              console.log("Error saving data to SharePoint:", error);
              setSubmitting(false);
              setError(true);
              setErrorMessage(
                "Error saving data to SharePoint: " + error.message
              );
            });
        })
        .catch((error) => {
          console.log("Error uploading file to SharePoint:", error);
          setSubmitting(false);
          setError(true);
          setErrorMessage(
            "Error uploading file to SharePoint: " + error.message
          );
        });
    } else {
      saveToSharePointAsync(formValues, token, captchaToken)
        .then(() => {
          console.log("Data saved to SharePoint");
          setSubmitting(false);
          setShowThankYou(true);
        })
        .catch((error) => {
          console.log("Error saving data to SharePoint:", error);
          setSubmitting(false);
          setError(true);
          setErrorMessage(
            "Error saving data to SharePoint: " + error.message
          );
        });
    }
  };

  const handleCloseThankYou = () => {
    setShowThankYou(false);
    handleClear();
  };

  const handleClear = () => {
    setFormValues(initialFormState);
    setValidated(false);
    setCaptchaToken("");
    recaptchaRef.current.reset();
    const fileControl = document.getElementById("attachment");
    if (fileControl) {
      fileControl.value = null;
    }
  };

  return (
    <Container className="form-container">
      <Row className="">
        <Col>
          <div className="header">
            <div className="logo">
              <br />
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="container-fluid">
            <img
              src={guardStopSign}
              alt="Synergy Guard with Stop Sign"
              className="full-width-image"
            />
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="pt-4">
            <h1>Welcome, Crossing Guards!</h1>
            <p>
              We're proud to support the School Crossing Guard Program. This registration form helps us identify who will be continuing their service, ensuring a smooth and respectful transition for everyone.
            </p>
            <p>
              At Synergy, we're committed to fostering an employee-focused culture grounded in respect, reliability, and community support. Complete the form below to let us know you’re ready to stay on and continue supporting your community.
            </p>
          </div>

          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <div className="heading-box">
              <h4 className="pb-3 pt-3 pl-2 gradient-heading">
                Contact Information
              </h4>
              <Row className="mb-3">
                <Form.Group as={Col} sm={4} xs={12} controlId="firstName">
                  <Form.Label>First Name*</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="firstName"
                    value={formValues.firstName}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide your first name.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} sm={4} xs={12} controlId="middleName">
                  <Form.Label>Middle Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="middleName"
                    value={formValues.middleName}
                    onChange={handleChange}
                  />
                </Form.Group>

                <Form.Group as={Col} sm={4} xs={12} controlId="lastName">
                  <Form.Label>Last Name*</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="lastName"
                    value={formValues.lastName}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide your last name.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={4} xs={12} controlId="phoneNumber">
                  <Form.Label>Phone Number*</Form.Label>
                  <Form.Control
                    required
                    pattern="[0-9]*"
                    type="text"
                    name="phoneNumber"
                    value={formValues.phoneNumber}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid phone number (numbers only).
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  sm={4}
                  xs={12}
                  controlId="alternativeNumber"
                >
                  <Form.Label>Alternative Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="alternativeNumber"
                    pattern="[0-9]*"
                    value={formValues.alternativeNumber}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid phone number (numbers only).
                  </Form.Control.Feedback>
                </Form.Group>
                <Form.Group as={Col} sm={4} xs={12} controlId="email">
                  <Form.Label>Email Address*</Form.Label>
                  <Form.Control
                    required
                    pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                    type="email"
                    name="email"
                    value={formValues.email}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid email address.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={4} xs={12} controlId="postalCode">
                  <Form.Label>Postal Code*</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="postalCode"
                    value={formValues.postalCode}
                    onChange={handleChange}
                    pattern="[A-Za-z][0-9][A-Za-z][0-9][A-Za-z][0-9]"
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid postal code (e.g., A1B2C3).
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>
            </div>

            <div className="heading-box">
              <h4 className="pb-3 pt-3 pl-2 gradient-heading">Employment</h4>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="isCurrentCrossingGuard">
                  <Form.Label>Are you a current Crossing Guard for the City/Municipality?*</Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="isCurrentCrossingGuard"
                    value={formValues.isCurrentCrossingGuard}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} sm={6} xs={12} controlId="cityMunicipality">
                  <Form.Label>Please identify the City or Municipality you currently work for.*</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="cityMunicipality"
                    value={formValues.cityMunicipality}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide the city or municipality you currently work for.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="continueRole">
                  <Form.Label>Are you interested in continuing your role as a crossing guard?*</Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="continueRole"
                    value={formValues.continueRole}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} sm={6} xs={12} controlId="startAvailability">
                  <Form.Label>
                    Are you available to start immediately for the upcoming school year, or do you require a specific start date?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="startAvailability"
                    value={formValues.startAvailability}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                {formValues.startAvailability === "No" && (
                  <Form.Group as={Col} sm={6} xs={12} controlId="preferredStartDate">
                    <Form.Label>Preferred Start Date*</Form.Label>
                    <Form.Control
                      required
                      type="date"
                      name="preferredStartDate"
                      value={formValues.preferredStartDate}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a preferred start date.
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="experience">
                  <Form.Label>Years of Experience*</Form.Label>
                  <Form.Control
                    name="yearsOfExperience"
                    onChange={handleChange}
                    as="select"
                    required
                    value={formValues.yearsOfExperience}
                  >
                    <option value="">Please select</option>
                    <option value="No Experience">No Experience</option>
                    <option value="1-3 Years">1-3 Years</option>
                    <option value="3-5 Years">3-5 Years</option>
                    <option value="5-10 Years">5-10 Years</option>
                    <option value="10+ Years">10+ Years</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please provide your years of experience.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  sm={6}
                  xs={12}
                  controlId="employmentStatus"
                >
                  <Form.Label>Are you interested in FT or PT work?*</Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="employmentStatus"
                    value={formValues.employmentStatus}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select your employment preference.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="availability">
                  <Form.Label>Availability*</Form.Label>
                  <Form.Select
                    required
                    name="availability"
                    value={formValues.availability}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    <option value="2 Shift Intersection">2 Shift Intersection</option>
                    <option value="3 Shift Intersection">3 Shift Intersection</option>
                    <option value="Casual On-Call">Casual On-Call</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    Please provide your availability.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  sm={6}
                  xs={12}
                  controlId="vulnerableSectorCheck"
                >
                  <Form.Label>
                    Do you have a Vulnerable Sector Check?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="hasVulnerableSectorCheck"
                    value={formValues.hasVulnerableSectorCheck}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select whether you have a Vulnerable Sector Check.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              {formValues.hasVulnerableSectorCheck === "Yes" && (
                <Row className="mb-3">
                  <Form.Group as={Col} sm={6} xs={12} controlId="expiryDate">
                    <Form.Label>Vulnerable Sector Check Expiry Date*</Form.Label>
                    <Form.Control
                      required
                      type="date"
                      name="expiryDate"
                      value={formValues.expiryDate}
                      onChange={handleChange}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide the expiry date.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Row>
              )}

              <Row className="mb-3">
                <Form.Group as={Col} sm={12} xs={12} controlId="intersection">
                  <Form.Label>
                    What intersection do you currently work primarily at?
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="currentIntersection"
                    value={formValues.currentIntersection}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>
            </div>

            <div className="heading-box">
              <h4 className="pb-3 pt-3 pl-2 gradient-heading">
                Other Information
              </h4>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="transportation">
                  <Form.Label>
                    Do you have access to reliable transportation?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="hasTransportation"
                    value={formValues.hasTransportation}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select whether you have access to reliable
                    transportation.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} sm={6} xs={12} controlId="smartphone">
                  <Form.Label>Do you have a smartphone with data?*</Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="hasSmartphone"
                    value={formValues.hasSmartphone}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select whether you have a smartphone with data.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="weatherComfort">
                  <Form.Label>
                    Are you comfortable working in adverse weather conditions, such as rain, heat, extreme cold, and snow?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="weatherComfort"
                    value={formValues.weatherComfort}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group as={Col} sm={6} xs={12} controlId="physicalAbility">
                  <Form.Label>
                    Are you able to stand for up to 2 hours and walk short
                    distances?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="physicalAbility"
                    value={formValues.physicalAbility}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group
                  as={Col}
                  sm={6}
                  xs={12}
                  controlId="independentWork"
                >
                  <Form.Label>
                    Are you comfortable working independently with minimal
                    supervision?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="independentWork"
                    value={formValues.independentWork}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group
                  as={Col}
                  sm={6}
                  xs={12}
                  controlId="publicInteraction"
                >
                  <Form.Label>
                    Are you comfortable interacting with the general public,
                    including children and parents?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="publicInteraction"
                    value={formValues.publicInteraction}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12} controlId="stopPaddleAbility">
                  <Form.Label>
                    Are you able to lift and hold a stop paddle (1.6 - 2.3 lbs) for brief periods?*
                  </Form.Label>
                  <Form.Control
                    required
                    as="select"
                    name="stopPaddleAbility"
                    value={formValues.stopPaddleAbility}
                    onChange={handleChange}
                  >
                    <option value="">-- Select --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    Please select an option.
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              <Row className="mb-3">
                <Form.Group as={Col} sm={12} xs={12} controlId="comments">
                  <Form.Label>
                    Is there anything we should be aware of to help ensure a smooth transition (e.g. accessibility needs, location changes, availability)?
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="comments"
                    value={formValues.comments}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Row>
            </div>

            <div className="heading-box">
              <h4 className="pb-3 pt-3 pl-2 gradient-heading">
                Upload CV (Optional)
              </h4>
              <Row className="mb-3">
                <Form.Group as={Col} sm={6} xs={12}>
                  <Form.Control
                    type="file"
                    name="attachment"
                    id="attachment"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <Form.Text>
                    We support the following file formats: PDF, DOC, DOCX & ZIP.
                  </Form.Text>
                </Form.Group>
              </Row>
            </div>

            <div className="error-message">
              {showError && (
                <Alert variant="danger">
                  Uh-oh! Please fix the errors in the form and try submitting
                  again.
                </Alert>
              )}
            </div>

            <Row>
              <Col>
                <ReCAPTCHA
                  sitekey="6LeO-WsmAAAAAPGv5ZldEjlIMAE3gmhSAV_znmlX"
                  onChange={handleCaptchaChange}
                  ref={recaptchaRef}
                />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col>
                <Form.Text className="text-secondary">
                  By submitting this registration form, you acknowledge that all
                  information provided is true and accurate to the best of your
                  knowledge. Thank you for your cooperation and trust; we are
                  looking forward to working with you.
                </Form.Text>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col>
                <Button
                  variant="primary"
                  type="submit"
                  className="me-3"
                >
                  {submitting ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    "Submit"
                  )}
                </Button>
                <Button
                  variant="secondary"
                  type="button"
                  className="ml-2"
                  onClick={handleClear}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className="footer">
            <p>
              For any questions or concerns, please contact our HR department at{" "}
              <a href="mailto:hr@getsynergy.ca">hr@getsynergy.ca</a>.
            </p>
          </div>
        </Col>
      </Row>

      <Modal show={showThankYou} onHide={handleCloseThankYou}>
        <Modal.Header closeButton onAbort={handleCloseThankYou}>
          <Modal.Title>Thank You!</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Your form has been successfully submitted.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseThankYou}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {error && (
        <Modal show={error} onHide={() => setError(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger">
              {errorMessage.indexOf("reCaptcha") === -1 ? (
                <>
                  Error processing form. Please try again later.
                  <br />
                  Error Details: {errorMessage}
                </>
              ) : (
                <>{errorMessage}</>
              )}
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setError(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </Container>
  );
}

export default App;